/**
 * Skeleton, lightweight loading placeholders shaped like the content they
 * replace. Pattern-matches the existing CSS classes (`.skeleton`,
 * `.skeleton-block`, `.skeleton-row`) defined in app.css — no new visual
 * vocabulary.
 *
 * Pair with `useDelayedFlag` (200ms by default) so fast loads never flash
 * the skeleton in-and-out. Per Ean 2026-06-02 "no constant teasing loads."
 *
 * Three variants cover the page shapes the app actually uses today:
 *
 *   - <SkeletonBlock />            single rectangular block (e.g., a card body)
 *   - <SkeletonLines count={n} />  n stacked text rows (e.g., a description)
 *   - <SkeletonCardGrid />         repeating tile-grid for catalog-like pages
 *   - <SkeletonDetail />           record-detail header + summary + body
 *
 * Don't reinvent — extend with new variants when an existing page needs a
 * shape these don't cover.
 */

interface SkeletonBlockProps {
  /** Pixel height; defaults to 40 (matches `.skeleton-block` default). */
  height?: number | string;
  /** Pixel/percent width; defaults to '100%'. */
  width?: number | string;
  /** Border radius override; defaults to 8px. */
  radius?: number | string;
  /** Extra margin top. */
  marginTop?: number | string;
}

export function SkeletonBlock({ height, width, radius, marginTop }: SkeletonBlockProps) {
  return (
    <div
      className="skeleton-block"
      aria-hidden="true"
      style={{
        ...(height !== undefined ? { height } : {}),
        ...(width !== undefined ? { width } : {}),
        ...(radius !== undefined ? { borderRadius: radius } : {}),
        ...(marginTop !== undefined ? { marginTop } : {}),
      }}
    />
  );
}

interface SkeletonLinesProps {
  count?: number;
  /** Optional varying widths so the lines don't read as a perfect grid. */
  widths?: Array<string | number>;
  marginTop?: number | string;
}

export function SkeletonLines({ count = 3, widths, marginTop }: SkeletonLinesProps) {
  const defaultWidths = ['92%', '78%', '85%', '60%'];
  return (
    <div
      aria-hidden="true"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        ...(marginTop !== undefined ? { marginTop } : {}),
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className="skeleton"
          style={{
            width: widths?.[i] ?? defaultWidths[i % defaultWidths.length],
            height: 12,
          }}
        />
      ))}
    </div>
  );
}

interface SkeletonCardGridProps {
  /** Number of placeholder cards; defaults to 6. */
  count?: number;
  /** Pixel height per card; defaults to 140. */
  cardHeight?: number;
}

export function SkeletonCardGrid({ count = 6, cardHeight = 140 }: SkeletonCardGridProps) {
  return (
    <div className="tile-grid" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="tile"
          style={{
            height: cardHeight,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span className="skeleton" style={{ width: 32, height: 32, borderRadius: 8 }} />
            <span className="skeleton" style={{ width: '50%', height: 14 }} />
          </div>
          <SkeletonLines count={2} widths={['90%', '60%']} />
          <span
            className="skeleton"
            style={{ width: 80, height: 18, marginTop: 'auto', borderRadius: 6 }}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * SkeletonTable — table-body skeleton. Push 5-F (2026-07-11) extracted from
 * three near-identical local defs (People.tsx, MyActivity.tsx,
 * activity/ActivityDispatches.tsx) so every table-loading surface renders the
 * same shape. Uses the `.skeleton-row` CSS class (app.css) which is purpose-
 * built for `<tr><td><div/></td></tr>` structure — SkeletonBlock would break
 * the row layout.
 */
interface SkeletonTableProps {
  rows: number;
  cols: number;
}

export function SkeletonTable({ rows, cols }: SkeletonTableProps) {
  return (
    <div className="table-wrap">
      <table className="table">
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r} className="skeleton-row">
              {Array.from({ length: cols }).map((__, c) => (
                <td key={c}>
                  <div style={{ width: c === 0 ? '70%' : '40%' }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Detail-page skeleton — header strip + summary + a couple of content blocks.
 * Matches the Lightning record-page shape (highlight bar → details card →
 * related lists) so the skeleton reads as the page becoming.
 */
export function SkeletonDetail() {
  return (
    <div aria-hidden="true">
      <div className="lightning-highlight">
        <div className="lightning-highlight-left">
          <span className="skeleton" style={{ width: 48, height: 48, borderRadius: 12 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span className="skeleton" style={{ width: 220, height: 20 }} />
            <span className="skeleton" style={{ width: 320, height: 12 }} />
          </div>
        </div>
      </div>
      <div className="card card-padded" style={{ marginBottom: 'var(--card-gap)' }}>
        <SkeletonLines count={3} widths={['85%', '70%', '60%']} />
      </div>
      <div className="card card-padded">
        <SkeletonBlock height={140} />
      </div>
    </div>
  );
}
